/**
 * VSM Flow Editor — drag-drop process flow with visible swimlanes,
 * multi-output decision blocks, lane-tinted blocks, and pain-points per block.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, addEdge, applyNodeChanges, applyEdgeChanges, Handle, Position, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash2, AlertTriangle, Box, Diamond, Clock, Database, X, Edit3, ArrowDownUp, ArrowLeftRight, Info } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Block + lane meta
const BLOCK_TYPES = {
  process:  { label: "Process",  icon: Box,      color: "#7C9A7E" },
  decision: { label: "Decision", icon: Diamond,  color: "#D97706" },
  wait:     { label: "Wait",     icon: Clock,    color: "#9CA3AF" },
  storage:  { label: "Storage",  icon: Database, color: "#6366F1" },
};

const SEVERITY_COLOR = { H: "#DC2626", M: "#D97706", L: "#3B82F6" };
const TIMWOOD_TAGS = ["Transport", "Inventory", "Motion", "Waiting", "Overprocessing", "Overproduction", "Defects", "Skills"];

const HANDLE_BASE = { width: 12, height: 12, background: "#7C9A7E", border: "2px solid white" };
const handleStyle = (color) => ({ ...HANDLE_BASE, background: color });

// ─────────────────────────────────────────────────────────────
// Custom node — Process / Wait / Storage (1 in / 1 out)
function ProcessNode({ data, id, selected }) {
  const Icon = BLOCK_TYPES[data.blockType]?.icon || Box;
  const blockColor = BLOCK_TYPES[data.blockType]?.color || "#7C9A7E";
  const laneColor = data.laneColor || blockColor;
  const painCount = (data.painpoints || []).length;
  return (
    <div data-testid={`vsm-node-${id}`} onDoubleClick={() => data.onEdit && data.onEdit(id)} className={`bg-white rounded-md border-2 shadow-sm min-w-[180px] overflow-hidden ${selected ? "ring-2 ring-sage-700" : ""}`} style={{ borderColor: laneColor }}>
      <Handle type="target" position={Position.Left} id="in" style={handleStyle(laneColor)} />
      <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ backgroundColor: `${blockColor}1A`, borderColor: blockColor }}>
        <Icon className="w-3.5 h-3.5" style={{ color: blockColor }} />
        <p className="font-semibold text-sm text-sage-900 flex-1 truncate">{data.label || "Step"}</p>
        {painCount > 0 && <span className="text-xs bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {painCount}</span>}
      </div>
      <div className="px-3 py-2 grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
        <span className="text-sage-500">CT</span><span className="text-right font-mono text-sage-800">{data.cycle_time_min || 0}m</span>
        <span className="text-sage-500">CO</span><span className="text-right font-mono text-sage-800">{data.changeover_min || 0}m</span>
        <span className="text-sage-500">Up%</span><span className="text-right font-mono text-sage-800">{data.uptime_pct || 0}%</span>
        <span className="text-sage-500">Def%</span><span className="text-right font-mono text-sage-800">{data.defect_pct || 0}%</span>
      </div>
      {data.laneLabel && <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white text-center" style={{ backgroundColor: laneColor }}>{data.laneLabel}</div>}
      <Handle type="source" position={Position.Right} id="out" style={handleStyle(laneColor)} />
    </div>
  );
}

// Decision node — 1 in / N outs (configurable branches), each with a separate handle
function DecisionNode({ data, id, selected }) {
  const blockColor = BLOCK_TYPES.decision.color;
  const laneColor = data.laneColor || blockColor;
  const branches = data.branches && data.branches.length > 0 ? data.branches : [
    { id: "yes", label: "Yes" },
    { id: "no",  label: "No" },
  ];
  const painCount = (data.painpoints || []).length;
  // Fixed row height per branch so handles never collide
  const rowHeight = 22;
  const headerH = 36;       // header bar height
  const branchTop = (i) => headerH + 12 + i * rowHeight;
  return (
    <div data-testid={`vsm-node-${id}`} onDoubleClick={() => data.onEdit && data.onEdit(id)} className={`relative bg-white rounded-md border-2 shadow-sm overflow-visible ${selected ? "ring-2 ring-sage-700" : ""}`} style={{ borderColor: laneColor, minWidth: 220 }}>
      <Handle type="target" position={Position.Left} id="in" style={{ ...handleStyle(laneColor), top: headerH / 2 + 8 }} />
      <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ backgroundColor: `${blockColor}1A`, borderColor: blockColor, height: headerH }}>
        <Diamond className="w-3.5 h-3.5" style={{ color: blockColor }} />
        <p className="font-semibold text-sm text-sage-900 flex-1 truncate">{data.label || "Decision"}</p>
        {painCount > 0 && <span className="text-xs bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {painCount}</span>}
      </div>
      <div className="px-3 py-2 text-xs">
        <p className="text-xs text-sage-500 italic mb-1">Outputs (drag from each)</p>
        <ul className="space-y-1" style={{ minHeight: branches.length * rowHeight }}>
          {branches.map((b) => (
            <li key={b.id} className="flex justify-between items-center" style={{ height: rowHeight - 4 }}>
              <span className="text-sage-800 font-semibold">{b.label}</span>
              <span className="text-sage-400 font-mono text-[11px]">→</span>
            </li>
          ))}
        </ul>
      </div>
      {data.laneLabel && <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-white text-center" style={{ backgroundColor: laneColor }}>{data.laneLabel}</div>}
      {/* Render one separately-positioned source handle per branch on the right edge */}
      {branches.map((b, i) => (
        <Handle
          key={b.id}
          type="source"
          position={Position.Right}
          id={b.id}
          style={{ ...handleStyle(laneColor), top: branchTop(i), right: -6 }}
        />
      ))}
    </div>
  );
}

// Lane container node — visible swimlane
function LaneNode({ data, selected }) {
  const isH = data.orientation === "horizontal";
  return (
    <div className={`relative rounded-md ${selected ? "ring-2 ring-sage-700" : ""}`} style={{ width: "100%", height: "100%", backgroundColor: `${data.color}12`, border: `2px dashed ${data.color}80`, boxSizing: "border-box" }}>
      <div className="absolute text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded shadow flex items-center gap-1" style={{
        backgroundColor: data.color,
        color: "white",
        ...(isH ? { top: 6, left: 6 } : { top: 6, left: "50%", transform: "translateX(-50%)" }),
      }}>
        {isH ? "↔" : "↕"} {data.label}
      </div>
    </div>
  );
}

const NODE_TYPES = { process: ProcessNode, decision: DecisionNode, wait: ProcessNode, storage: ProcessNode, lane: LaneNode, cell: CellNode };

// Density-heatmap cell: tints lane intersections by block / pain-point density
function CellNode({ data }) {
  const intensity = Math.min(1, data.count / Math.max(1, data.maxCount));
  const alpha = 0.05 + intensity * 0.35;
  return (
    <div style={{ width: "100%", height: "100%", backgroundColor: `rgba(217, 119, 6, ${alpha})`, pointerEvents: "none" }}>
      {data.count > 0 && (
        <div style={{ position: "absolute", bottom: 4, right: 4, background: "white", border: "1px solid #D97706", color: "#92400E", fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 8 }}>
          {data.count}{data.metric === "pain" ? "⚠" : ""}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Editor — H lanes span full width (process phases), V lanes span full
// height (performers/roles). They visually OVERLAP forming a matrix.
// Each block can belong to one H lane + one V lane; we snap on drag-stop.
const H_HEIGHT = 200;
const V_WIDTH = 280;
const MIN_W = 1200;
const MIN_H = 600;
const PAD = 20;

function computeLaneLayout(lanes) {
  const hLanes = lanes.filter(l => (l.orientation || "horizontal") === "horizontal");
  const vLanes = lanes.filter(l => l.orientation === "vertical");
  const totalH = Math.max(MIN_H, hLanes.length * H_HEIGHT + PAD * 2);
  const totalW = Math.max(MIN_W, vLanes.length * V_WIDTH + PAD * 2);
  const positions = {};
  hLanes.forEach((l, i) => {
    positions[l.id] = { x: PAD, y: PAD + i * H_HEIGHT, width: totalW - PAD * 2, height: H_HEIGHT, kind: "h" };
  });
  vLanes.forEach((l, i) => {
    positions[l.id] = { x: PAD + i * V_WIDTH, y: PAD, width: V_WIDTH, height: totalH - PAD * 2, kind: "v" };
  });
  return { positions, totalW, totalH };
}

// Find which H/V lanes contain a given (x,y) point given current layout
function findLanesAtPoint(layout, x, y) {
  const result = { h_lane_id: null, v_lane_id: null };
  Object.entries(layout.positions).forEach(([laneId, p]) => {
    if (x >= p.x && x <= p.x + p.width && y >= p.y && y <= p.y + p.height) {
      if (p.kind === "h") result.h_lane_id = laneId;
      if (p.kind === "v") result.v_lane_id = laneId;
    }
  });
  return result;
}

export default function VsmFlowEditor({ value, onChange }) {
  // Backwards compatibility: old shape had top-level `orientation`. Each lane now owns its own.
  const initialLanes = (() => {
    if (value?.lanes?.length) {
      return value.lanes.map(l => ({ ...l, orientation: l.orientation || value.orientation || "horizontal" }));
    }
    return [{ id: "lane-1", label: "Process Phase", color: "#7C9A7E", orientation: "horizontal" }];
  })();
  const [lanes, setLanes] = useState(initialLanes);
  // Migrate legacy `lane_id` → `h_lane_id` so existing data keeps tinting
  const [nodes, setNodes] = useState((value?.nodes || []).map(n => {
    if (n.data && n.data.lane_id && !n.data.h_lane_id && !n.data.v_lane_id) {
      const refLane = initialLanes.find(l => l.id === n.data.lane_id);
      const isV = refLane?.orientation === "vertical";
      return { ...n, data: { ...n.data, [isV ? "v_lane_id" : "h_lane_id"]: n.data.lane_id } };
    }
    return n;
  }));
  const [edges, setEdges] = useState(value?.edges || []);
  const [editingId, setEditingId] = useState(null);
  const [heatmap, setHeatmap] = useState(null); // null | "blocks" | "pain"
  const initialised = useRef(false);

  const layout = useMemo(() => computeLaneLayout(lanes), [lanes]);

  // Build lane container nodes — H lanes z=0, V lanes z=1 (still below blocks at z=5)
  const laneNodes = useMemo(() => {
    return lanes.map((l) => {
      const pos = layout.positions[l.id];
      if (!pos) return null;
      return {
        id: l.id,
        type: "lane",
        position: { x: pos.x, y: pos.y },
        data: { label: l.label, color: l.color, orientation: l.orientation || "horizontal" },
        draggable: false,
        selectable: false,
        style: { width: pos.width, height: pos.height, zIndex: pos.kind === "v" ? 1 : 0, pointerEvents: "none" },
      };
    }).filter(Boolean);
  }, [lanes, layout]);

  const laneById = useMemo(() => Object.fromEntries(lanes.map(l => [l.id, l])), [lanes]);

  // Density-heatmap cells: one per (H × V) lane intersection
  const cellNodes = useMemo(() => {
    if (!heatmap) return [];
    const hLanes = lanes.filter(l => (l.orientation || "horizontal") === "horizontal");
    const vLanes = lanes.filter(l => l.orientation === "vertical");
    if (!hLanes.length || !vLanes.length) return [];
    const blocks = nodes.filter(n => n.type !== "lane");
    const cells = [];
    let maxCount = 1;
    hLanes.forEach((h) => vLanes.forEach((v) => {
      const matched = blocks.filter(b => b.data?.h_lane_id === h.id && b.data?.v_lane_id === v.id);
      const count = heatmap === "pain" ? matched.reduce((a, b) => a + (b.data?.painpoints?.length || 0), 0) : matched.length;
      maxCount = Math.max(maxCount, count);
      const hp = layout.positions[h.id]; const vp = layout.positions[v.id];
      if (!hp || !vp) return;
      cells.push({ id: `cell-${h.id}-${v.id}`, h, v, hp, vp, count });
    }));
    return cells.map(c => ({
      id: c.id, type: "cell",
      position: { x: c.vp.x, y: c.hp.y },
      data: { count: c.count, maxCount, metric: heatmap },
      draggable: false, selectable: false,
      style: { width: c.vp.width, height: c.hp.height, zIndex: 2, pointerEvents: "none" },
    }));
  }, [heatmap, lanes, nodes, layout]);

  // Notify parent
  useEffect(() => {
    if (!initialised.current) { initialised.current = true; return; }
    onChange({ lanes, nodes, edges });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, nodes, edges]);

  const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)), []);
  const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)), []);
  const onConnect = useCallback((c) => setEdges((es) => addEdge({
    ...c,
    type: "smoothstep",
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#4B5E4D", width: 20, height: 20 },
    style: { stroke: "#4B5E4D", strokeWidth: 2 },
    label: c.sourceHandle && c.sourceHandle !== "out" ? c.sourceHandle : undefined,
    labelStyle: { fontSize: 11, fontWeight: 600, fill: "#4B5E4D" },
    labelBgStyle: { fill: "white", fillOpacity: 0.9 },
    labelBgPadding: [4, 2],
  }, es)), []);

  // Snap-to-lane on drag stop: detect lanes covering the block centre + update its lane refs
  const onNodeDragStop = useCallback((evt, node) => {
    if (node.type === "lane") return;
    // Approximate block dimensions — width ~220, height ~110
    const cx = node.position.x + 110;
    const cy = node.position.y + 55;
    const found = findLanesAtPoint(layout, cx, cy);
    if (found.h_lane_id || found.v_lane_id) {
      setNodes((ns) => ns.map(n => n.id === node.id ? { ...n, data: { ...n.data,
        ...(found.h_lane_id ? { h_lane_id: found.h_lane_id } : {}),
        ...(found.v_lane_id ? { v_lane_id: found.v_lane_id } : {}),
      } } : n));
    }
  }, [layout]);

  const addBlock = (blockType) => {
    const id = `n-${Date.now()}`;
    const firstH = lanes.find(l => (l.orientation || "horizontal") === "horizontal");
    const firstV = lanes.find(l => l.orientation === "vertical");
    const px = (firstV ? layout.positions[firstV.id].x : PAD) + 30;
    const py = (firstH ? layout.positions[firstH.id].y : PAD) + 40;
    const blockCount = nodes.length;
    const stride = 220;
    const newNode = {
      id, type: blockType,
      position: { x: px + (blockCount % 4) * stride, y: py + Math.floor(blockCount / 4) * 130 },
      data: {
        blockType,
        label: BLOCK_TYPES[blockType].label,
        cycle_time_min: 0, changeover_min: 0, uptime_pct: 100, defect_pct: 0,
        operators: 1, inventory_units: 0,
        h_lane_id: firstH?.id || null,
        v_lane_id: firstV?.id || null,
        painpoints: [],
        ...(blockType === "decision" ? { branches: [{ id: "yes", label: "Yes" }, { id: "no", label: "No" }] } : {}),
      },
      style: { zIndex: 5 },
    };
    setNodes((n) => [...n, newNode]);
  };

  const updateNode = (id, patch) => {
    setNodes((nodes) => nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
  };

  const removeNode = (id) => {
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    if (editingId === id) setEditingId(null);
  };

  const addLane = (orientation = "horizontal") => {
    const colors = ["#7C9A7E", "#D97706", "#6366F1", "#DC2626", "#0EA5E9", "#A855F7"];
    const defLabel = orientation === "horizontal" ? `Phase ${lanes.filter(l => l.orientation === "horizontal").length + 1}` : `Performer ${lanes.filter(l => l.orientation === "vertical").length + 1}`;
    setLanes((l) => [...l, { id: `lane-${Date.now()}`, label: defLabel, color: colors[l.length % colors.length], orientation }]);
  };
  const updateLane = (id, patch) => setLanes((ls) => ls.map((l) => l.id === id ? { ...l, ...patch } : l));
  const removeLane = (id) => {
    if (lanes.length <= 1) return;
    setLanes((ls) => ls.filter((l) => l.id !== id));
    setNodes((ns) => ns.map((n) => {
      const p = { ...n.data };
      if (p.h_lane_id === id) p.h_lane_id = null;
      if (p.v_lane_id === id) p.v_lane_id = null;
      return { ...n, data: p };
    }));
  };
  const toggleLaneOrientation = (id) => {
    setLanes((ls) => ls.map((l) => l.id === id ? { ...l, orientation: (l.orientation || "horizontal") === "horizontal" ? "vertical" : "horizontal" } : l));
  };

  // Inject lane meta + onEdit closure into each node every render so blocks reflect lane colour live.
  // V-lane (performer) wins for tint since it answers "who"; fall back to H-lane (phase) or first lane.
  const nodesWithHandlers = nodes.map((n) => {
    const hLane = laneById[n.data?.h_lane_id];
    const vLane = laneById[n.data?.v_lane_id];
    const tint = vLane || hLane || laneById[n.data?.lane_id] || lanes[0];
    const label = [hLane?.label, vLane?.label].filter(Boolean).join(" · ") || tint?.label || "";
    return {
      ...n,
      data: {
        ...n.data,
        onEdit: setEditingId,
        laneColor: tint?.color,
        laneLabel: label,
      },
    };
  });
  const editing = editingId ? nodes.find((n) => n.id === editingId) : null;

  return (
    <ReactFlowProvider>
      <div className="border border-[#E2E8E3] rounded-md overflow-hidden bg-white" data-testid="vsm-flow-editor">
        <div className="p-3 border-b border-sage-100 bg-sage-50 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-sage-600 uppercase tracking-wider">Add block:</span>
          {Object.entries(BLOCK_TYPES).map(([k, t]) => {
            const Icon = t.icon;
            return (
              <button key={k} data-testid={`add-block-${k}`} onClick={() => addBlock(k)} className="flex items-center gap-1 border border-sage-200 bg-white text-sage-700 rounded-md px-2.5 py-1 text-xs font-semibold hover:bg-sage-100">
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
          <span className="mx-2 text-sage-300">|</span>
          <button data-testid="add-lane-h" onClick={() => addLane("horizontal")} className="flex items-center gap-1 border border-sage-200 bg-white text-sage-700 rounded-md px-2.5 py-1 text-xs font-semibold hover:bg-sage-100"><ArrowLeftRight className="w-3 h-3" /> Add H-lane</button>
          <button data-testid="add-lane-v" onClick={() => addLane("vertical")} className="flex items-center gap-1 border border-sage-200 bg-white text-sage-700 rounded-md px-2.5 py-1 text-xs font-semibold hover:bg-sage-100"><ArrowDownUp className="w-3 h-3" /> Add V-lane</button>
          <span className="mx-2 text-sage-300">|</span>
          <span className="text-xs font-semibold text-sage-600 uppercase tracking-wider">Heatmap:</span>
          <button data-testid="heatmap-off" onClick={() => setHeatmap(null)} className={`text-xs font-semibold px-2 py-1 rounded-md border ${heatmap === null ? "bg-sage-700 text-white border-sage-700" : "bg-white text-sage-700 border-sage-200 hover:bg-sage-100"}`}>Off</button>
          <button data-testid="heatmap-blocks" onClick={() => setHeatmap("blocks")} className={`text-xs font-semibold px-2 py-1 rounded-md border ${heatmap === "blocks" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-sage-700 border-sage-200 hover:bg-sage-100"}`}>Blocks</button>
          <button data-testid="heatmap-pain" onClick={() => setHeatmap("pain")} className={`text-xs font-semibold px-2 py-1 rounded-md border ${heatmap === "pain" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-sage-700 border-sage-200 hover:bg-sage-100"}`}>Pain</button>
          <span className="ml-auto text-xs text-sage-400">{nodes.length} blocks · {edges.length} arrows · {lanes.length} lanes</span>
        </div>

        <div className="p-2 border-b border-sage-100 bg-white flex items-center gap-2 flex-wrap" data-testid="lanes-editor">
          <Info className="w-3 h-3 text-sage-400" />
          <span className="text-xs text-sage-500">Each lane has its own orientation. Click ↔/↕ to switch a single lane.</span>
          {lanes.map((l) => {
            const isH = (l.orientation || "horizontal") === "horizontal";
            return (
              <div key={l.id} className="flex items-center gap-1 border-2 rounded-md px-2 py-0.5 text-xs" style={{ borderColor: l.color }}>
                <input type="color" value={l.color} onChange={(e) => updateLane(l.id, { color: e.target.value })} className="w-5 h-5 rounded cursor-pointer border-0 p-0" />
                <input value={l.label} onChange={(e) => updateLane(l.id, { label: e.target.value })} className="bg-transparent w-32 outline-none text-sage-800" data-testid={`lane-label-${l.id}`} />
                <button data-testid={`lane-orient-${l.id}`} onClick={() => toggleLaneOrientation(l.id)} title={isH ? "Switch to vertical" : "Switch to horizontal"} className="text-sage-600 hover:text-sage-900 px-1 font-bold">{isH ? "↔" : "↕"}</button>
                {lanes.length > 1 && <button onClick={() => removeLane(l.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>}
              </div>
            );
          })}
        </div>

        <div style={{ height: 600 }} className="bg-[#FAFAF7]">
          <ReactFlow
            nodes={[...laneNodes, ...cellNodes, ...nodesWithHandlers]}
            edges={edges}
            onNodesChange={onNodesChange}
            onNodeDragStop={onNodeDragStop}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            connectionLineStyle={{ stroke: "#4B5E4D", strokeWidth: 2 }}
            connectionLineType="smoothstep"
            defaultEdgeOptions={{ type: "smoothstep", animated: true, markerEnd: { type: MarkerType.ArrowClosed, color: "#4B5E4D" }, style: { stroke: "#4B5E4D", strokeWidth: 2 } }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#C5D6C7" gap={16} />
            <Controls />
            <MiniMap nodeStrokeColor="#4B5E4D" nodeColor={(n) => n.type === "lane" ? `${n.data.color}40` : "#7C9A7E"} pannable zoomable />
          </ReactFlow>
        </div>
        <div className="px-3 py-2 bg-sage-50 border-t border-sage-100 text-xs text-sage-500 flex items-center gap-2">
          <Info className="w-3 h-3" /> <strong>Tip:</strong> Drag from a block&#39;s coloured circle to another block to draw an arrow. Decision blocks have one labelled output per branch — drag from each.
        </div>
        <CanvasPainPointsTable nodes={nodes} onJump={setEditingId} />
      </div>

      {editing && <BlockEditor node={editing} updateNode={updateNode} removeNode={removeNode} onClose={() => setEditingId(null)} lanes={lanes} />}
    </ReactFlowProvider>
  );
}

// ─────────────────────────────────────────────────────────────
// Roll-up pain-points table that lives directly below the canvas
function CanvasPainPointsTable({ nodes, onJump }) {
  const rows = [];
  nodes.forEach((n) => {
    if (n.type === "lane") return;
    (n.data?.painpoints || []).forEach((p) => {
      rows.push({ ...p, node_id: n.id, node_label: n.data?.label || "—" });
    });
  });
  if (rows.length === 0) {
    return (
      <div className="px-4 py-3 border-t border-sage-100 bg-white" data-testid="canvas-painpoints-empty">
        <p className="text-xs text-sage-400 italic flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> No pain-points yet — double-click any block to add one.</p>
      </div>
    );
  }
  const order = { H: 0, M: 1, L: 2 };
  rows.sort((a, b) => (order[a.severity] || 99) - (order[b.severity] || 99));
  return (
    <div className="border-t border-sage-100 bg-white" data-testid="canvas-painpoints-table">
      <div className="px-4 py-2 flex items-center gap-2 border-b border-sage-100 bg-sage-50">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <h4 className="font-heading text-sm font-semibold text-sage-800">Pain-points roll-up</h4>
        <span className="text-xs text-sage-500">({rows.length} across {new Set(rows.map(r => r.node_id)).size} blocks)</span>
      </div>
      <div className="max-h-48 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5">Sev</th>
              <th className="text-left px-3 py-1.5">Block</th>
              <th className="text-left px-3 py-1.5">Pain-point</th>
              <th className="text-left px-3 py-1.5">Waste</th>
              <th className="text-left px-3 py-1.5">Owner</th>
              <th className="text-right px-3 py-1.5">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-sage-100 hover:bg-sage-50 cursor-pointer" onClick={() => onJump(p.node_id)} data-testid={`painpoint-row-${p.id}`}>
                <td className="px-3 py-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: SEVERITY_COLOR[p.severity] }}></span> <span className="text-sage-700 font-semibold">{p.severity}</span></td>
                <td className="px-3 py-1.5 text-sage-800">{p.node_label}</td>
                <td className="px-3 py-1.5 font-semibold text-sage-900">{p.title}</td>
                <td className="px-3 py-1.5"><span className="bg-sage-100 text-sage-700 rounded px-1.5 py-0.5">{p.waste_tag}</span></td>
                <td className="px-3 py-1.5 text-sage-600">{p.owner || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono text-sage-800">{p.cost_impact > 0 ? `$${p.cost_impact}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Block detail modal
function BlockEditor({ node, updateNode, removeNode, onClose, lanes }) {
  const d = node.data;
  const [pp, setPp] = useState({ title: "", severity: "M", waste_tag: "Waiting", owner: "", cost_impact: 0 });
  const [newBranch, setNewBranch] = useState("");
  const points = d.painpoints || [];
  const branches = d.branches || [];
  const isDecision = d.blockType === "decision";

  const setMeta = (patch) => updateNode(node.id, patch);
  const addPainpoint = () => {
    if (!pp.title.trim()) return;
    updateNode(node.id, { painpoints: [...points, { ...pp, id: `pp-${Date.now()}` }] });
    setPp({ title: "", severity: "M", waste_tag: "Waiting", owner: "", cost_impact: 0 });
  };
  const removePainpoint = (id) => updateNode(node.id, { painpoints: points.filter(p => p.id !== id) });
  const addBranch = () => {
    if (!newBranch.trim()) return;
    const id = newBranch.toLowerCase().replace(/[^a-z0-9]/g, "_") || `b${branches.length + 1}`;
    updateNode(node.id, { branches: [...branches, { id, label: newBranch }] });
    setNewBranch("");
  };
  const removeBranch = (id) => updateNode(node.id, { branches: branches.filter(b => b.id !== id) });
  const renameBranch = (id, label) => updateNode(node.id, { branches: branches.map(b => b.id === id ? { ...b, label } : b) });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="block-editor-modal">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-sage-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2"><Edit3 className="w-4 h-4 text-sage-600" /><h3 className="font-heading text-base font-semibold text-sage-900">Edit block</h3></div>
          <button onClick={onClose} className="text-sage-400 hover:text-sage-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><label className="text-xs font-semibold text-sage-600">Label</label><input data-testid="block-label" value={d.label} onChange={(e) => setMeta({ label: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Type</label><select value={d.blockType} onChange={(e) => setMeta({ blockType: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm bg-white">{Object.entries(BLOCK_TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-sage-600">H-lane (Phase)</label><select data-testid="block-h-lane" value={d.h_lane_id || ""} onChange={(e) => setMeta({ h_lane_id: e.target.value || null })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm bg-white"><option value="">— none —</option>{lanes.filter(l => (l.orientation || "horizontal") === "horizontal").map(l => <option key={l.id} value={l.id}>{l.label}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-sage-600">V-lane (Performer)</label><select data-testid="block-v-lane" value={d.v_lane_id || ""} onChange={(e) => setMeta({ v_lane_id: e.target.value || null })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm bg-white"><option value="">— none —</option>{lanes.filter(l => l.orientation === "vertical").map(l => <option key={l.id} value={l.id}>{l.label}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-sage-600">Cycle time (min)</label><input data-testid="block-ct" type="number" value={d.cycle_time_min || 0} onChange={(e) => setMeta({ cycle_time_min: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Changeover (min)</label><input type="number" value={d.changeover_min || 0} onChange={(e) => setMeta({ changeover_min: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Uptime %</label><input type="number" value={d.uptime_pct || 0} onChange={(e) => setMeta({ uptime_pct: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Defect %</label><input type="number" value={d.defect_pct || 0} onChange={(e) => setMeta({ defect_pct: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Operators</label><input type="number" value={d.operators || 1} onChange={(e) => setMeta({ operators: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-sage-600">Inventory (units)</label><input type="number" value={d.inventory_units || 0} onChange={(e) => setMeta({ inventory_units: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          </div>

          {isDecision && (
            <div data-testid="branches-editor">
              <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2 flex items-center gap-2"><Diamond className="w-4 h-4 text-amber-600" /> Decision branches <span className="text-xs text-sage-500 font-normal">({branches.length})</span></h4>
              <p className="text-xs text-sage-500 mb-2">Each branch creates a separate output handle on the block. Drag from a handle to connect.</p>
              <div className="space-y-1.5">
                {branches.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 bg-sage-50 rounded-md p-2">
                    <input value={b.label} onChange={(e) => renameBranch(b.id, e.target.value)} className="flex-1 bg-white border border-sage-200 rounded-md px-2 py-1 text-sm" />
                    <span className="text-xs font-mono text-sage-400">{b.id}</span>
                    {branches.length > 1 && <button onClick={() => removeBranch(b.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input data-testid="new-branch-input" placeholder="New branch label (e.g. Maybe, Escalate)" value={newBranch} onChange={(e) => setNewBranch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBranch()} className="flex-1 border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                <button data-testid="add-branch-button" onClick={addBranch} className="bg-sage-600 text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-sage-700">Add</button>
              </div>
            </div>
          )}

          <div>
            <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Pain-points <span className="text-xs text-sage-500 font-normal">({points.length})</span></h4>
            {points.length > 0 && (
              <div className="space-y-1.5 mb-3" data-testid="painpoints-list">
                {points.map(p => (
                  <div key={p.id} className="flex items-center gap-2 bg-sage-50 rounded-md p-2 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SEVERITY_COLOR[p.severity] }}></span>
                    <span className="font-semibold text-sage-900 flex-1 truncate">{p.title}</span>
                    <span className="bg-white border border-sage-200 rounded px-1.5 py-0.5 text-sage-600">{p.waste_tag}</span>
                    {p.cost_impact > 0 && <span className="text-sage-500">${p.cost_impact}</span>}
                    <button onClick={() => removePainpoint(p.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 bg-sage-50 rounded-md p-2" data-testid="painpoint-add">
              <input data-testid="painpoint-title" placeholder="Pain-point title" value={pp.title} onChange={(e) => setPp({ ...pp, title: e.target.value })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-xs" />
              <select value={pp.severity} onChange={(e) => setPp({ ...pp, severity: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white"><option value="H">High</option><option value="M">Medium</option><option value="L">Low</option></select>
              <select value={pp.waste_tag} onChange={(e) => setPp({ ...pp, waste_tag: e.target.value })} className="border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white">{TIMWOOD_TAGS.map(w => <option key={w}>{w}</option>)}</select>
              <button data-testid="painpoint-add-button" onClick={addPainpoint} className="bg-sage-600 text-white rounded-md px-2 py-1.5 text-xs font-semibold hover:bg-sage-700">Add</button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-sage-100 flex justify-between sticky bottom-0 bg-white">
          <button data-testid="block-delete" onClick={() => { removeNode(node.id); onClose(); }} className="text-red-600 text-sm font-semibold flex items-center gap-1 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Delete block</button>
          <button onClick={onClose} className="bg-sage-600 text-white rounded-md px-4 py-1.5 text-sm font-semibold hover:bg-sage-700">Done</button>
        </div>
      </div>
    </div>
  );
}

// Helpers used by VsmWizard diagnostic
export function collectPainPoints(flow) {
  const all = [];
  (flow?.nodes || []).forEach((n) => {
    (n.data?.painpoints || []).forEach((p) => {
      all.push({ ...p, node_id: n.id, node_label: n.data?.label || "—" });
    });
  });
  const order = { H: 0, M: 1, L: 2 };
  return all.sort((a, b) => (order[a.severity] || 99) - (order[b.severity] || 99));
}

export function aggregateBlocks(flow) {
  const blocks = (flow?.nodes || []).filter(n => n.type !== "lane");
  const laneLabel = {};
  (flow?.lanes || []).forEach(l => { laneLabel[l.id] = l.label; });
  return blocks.map(n => ({
    id: n.id,
    label: n.data?.label || "—",
    type: n.data?.blockType || n.type || "process",
    lane_id: n.data?.lane_id, // legacy
    h_lane_id: n.data?.h_lane_id || null,
    v_lane_id: n.data?.v_lane_id || null,
    h_lane_label: laneLabel[n.data?.h_lane_id] || "—",
    v_lane_label: laneLabel[n.data?.v_lane_id] || "—",
    cycle_time_min: Number(n.data?.cycle_time_min || 0),
    changeover_min: Number(n.data?.changeover_min || 0),
    uptime_pct: Number(n.data?.uptime_pct || 0),
    defect_pct: Number(n.data?.defect_pct || 0),
    operators: Number(n.data?.operators || 0),
    inventory_units: Number(n.data?.inventory_units || 0),
    painpoint_count: (n.data?.painpoints || []).length,
  }));
}
